import {
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { find, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { Model } from '../../gql/graphql';
import { useViewer } from '../common/ForgeViewer';

type LayersMenuListCheckBoxesProps = {
  setSelectedLayers: (layers: Set<string>) => void;
  selectedLayers: Set<string>;
  models: Pick<Model, 'ifcLayers' | 'revitLevels'>[];
};

export function possibleLayerTypes(
  models: Pick<Model, 'ifcLayers' | 'revitLevels'>[]
): Set<string> {
  return new Set(
    models.flatMap((model) => [
      ...(model.ifcLayers ?? []),
      ...(model.revitLevels ?? []),
    ])
  );
}

export default function LayersMenuListCheckBoxes({
  setSelectedLayers,
  selectedLayers,
  models,
}: LayersMenuListCheckBoxesProps): React.ReactElement {
  const {
    isAllModelsLoaded,
    loadedModels,
    visibilityManager: { hiddenModelUrns },
  } = useViewer();
  const [eligibleLayers, setEligibleLayers] = useState<Set<string>>(new Set());

  useEffect(() => {
    const findAndSetEligibleLayers = async () => {
      if (isAllModelsLoaded && !isEmpty(loadedModels)) {
        const newEligibleLayers = new Set<string>();

        for (const modelUrn of Object.keys(loadedModels)) {
          if (modelUrn in hiddenModelUrns) {
            continue;
          }
          const model = loadedModels[modelUrn];
          invariant(model, 'model not found');
          const ifcClassDbIds = await model.findProperty('Layer');
          const revitCategoryDbIds = await model.findProperty('Category');

          const ifcLayersInModel: string[] = await new Promise((resolve) => {
            model.getBulkProperties2(
              ifcClassDbIds,
              {
                propFilter: ['Layer'],
              },
              (propertyResults) => {
                resolve(
                  propertyResults.map(
                    (result) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      find(result.properties, ['attributeName', 'Layer'])!
                        .displayValue as string
                  )
                );
              }
            );
          });

          const revitLevelsInModel: string[] = await new Promise((resolve) => {
            model.getBulkProperties2(
              revitCategoryDbIds,
              {
                propFilter: ['Level', 'Base Constraint'],
                ignoreHidden: true,
              },
              (propertyResults) => {
                resolve(
                  propertyResults.map((result) => {
                    const level = find(result.properties, [
                      'attributeName',
                      'Level',
                    ]);
                    const baseConstraint = find(result.properties, [
                      'attributeName',
                      'Base Constraint',
                    ]);

                    if (
                      level &&
                      level.displayCategory === 'Constraints' &&
                      level.displayValue !== 'None'
                    ) {
                      return level.displayValue as string;
                    }

                    if (
                      baseConstraint &&
                      baseConstraint.displayCategory === 'Constraints' &&
                      baseConstraint.displayValue !== 'None' &&
                      baseConstraint.displayValue !== 'Unconnected'
                    ) {
                      return baseConstraint.displayValue as string;
                    }

                    return '';
                  })
                );
              }
            );
          });

          for (const level of new Set([
            ...ifcLayersInModel,
            ...revitLevelsInModel.filter((level) => level !== ''),
          ])) {
            newEligibleLayers.add(level);
          }
        }

        setEligibleLayers(newEligibleLayers);
      }
    };

    findAndSetEligibleLayers();
  }, [hiddenModelUrns, isAllModelsLoaded, loadedModels]);

  const handleChange = (selectedLayers: string | string[]) => {
    setSelectedLayers(
      typeof selectedLayers === 'string'
        ? new Set([selectedLayers])
        : new Set(selectedLayers)
    );
  };

  const handleAllEvent = (allEvent: string | string[]) => {
    //Allevent.length > 0, All checked
    if (allEvent.length > 0) {
      const allLayers = possibleLayerTypes(models);
      setSelectedLayers(allLayers);
    } else {
      setSelectedLayers(new Set());
    }
  };

  const { t } = useTranslation('categories');

  return (
    <MenuList maxHeight={'80vh'} overflow={'auto'} zIndex={99}>
      <MenuOptionGroup
        onChange={(e: string | string[]) => handleAllEvent(e)}
        type="checkbox"
        value={
          possibleLayerTypes(models).size === selectedLayers.size ? ['all'] : []
        }
      >
        <MenuItemOption value="all" key={'all'}>
          {t('all')}
        </MenuItemOption>
      </MenuOptionGroup>
      <MenuDivider />
      <MenuOptionGroup
        onChange={(e: string | string[]) => handleChange(e)}
        type="checkbox"
        value={Array.from(selectedLayers)}
      >
        {Array.from(eligibleLayers)
          .sort()
          .map((layer) => {
            return (
              <MenuItemOption value={layer} key={layer}>
                {layer}
              </MenuItemOption>
            );
          })}
      </MenuOptionGroup>
    </MenuList>
  );
}
